import {
  IScreenshotSettings,
  ScreenshotError,
  ScreenshotNotFocusedError
} from "models/screenshot";

import { copyMapToClipboard } from "components/map/utils";
import {
  captureToBlob,
  getChartPlotSelector
} from "components/screenshot/utils/captureElementToClipboard";

/**
 * A widget in screenshot mode has been moved into the fullscreen sheet, so
 * that is where its shot is read from. Charts have always preferred it
 * (`getChartPlotSelector`); the viewers now do too, because the well card can
 * put its own viewer in the sheet while a dashboard widget of the same kind
 * sits in the grid, and document order would otherwise hand back the grid's.
 */
const preferFullscreen = (className: string): string =>
  document.querySelector(`.fullscreen ${className}`)
    ? `.fullscreen ${className}`
    : `.App ${className}`;

/** Map widgetType to the CSS selector for its screenshot target element. */
const getWidgetSelector = (widgetType: string): string => {
  switch (widgetType) {
    case "chart":
    case "mpc":
      return getChartPlotSelector();
    case "sam":
    case "sam-rf-pv":
      return ".App .ChartPlot";
    case "threeDViewer":
      return preferFullscreen(".three-d-viewer-container");
    case "xdaViewer":
      return preferFullscreen(".xda-viewer-container");
    default:
      throw new Error(`Unsupported widget type`);
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const copyToClipboard = async (
  widgetType: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  widgetState: any,
  settings: IScreenshotSettings,
  screenshotOverlay: HTMLDivElement
): Promise<Blob | void> => {
  let blob: Blob | null = null;
  if (widgetType === "map") {
    blob = await copyMapToClipboard(widgetState, settings);
  } else {
    if (screenshotOverlay) {
      screenshotOverlay.style.border = "none";
      screenshotOverlay.style.outline = "none";
    }

    const element = document.querySelector(getWidgetSelector(widgetType)) as HTMLElement;
    blob = await captureToBlob(element);
  }

  if (!blob) return;
  if (settings.automation) {
    return blob;
  }
  if (!navigator.clipboard) {
    throw new ScreenshotError("Error copying to clipboard");
  }
  const data = [new ClipboardItem({ "image/png": blob })];

  if (data && document.hasFocus()) {
    try {
      await navigator.clipboard.write(data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      throw new ScreenshotError("Error copying to clipboard");
    }
  } else {
    // Prevent "Uncaught (in promise) NotAllowedError: "Failed to execute 'write' on 'Clipboard':""
    throw new ScreenshotNotFocusedError("Error copying XDA to clipboard");
  }
  return blob;
};

export default copyToClipboard;
